import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    ADMINS_REQUESTED,
    DELETE_ADMINS_REQUESTED,
    ADD_ADMINS_REQUESTED,
    CITIZEN_SCIENTIST_REQUESTED,
    ADD_CITIZEN_SCIENTIST_REQUESTED,
    DELETE_CITIZEN_SCIENTIST_REQUESTED,
    ADD_CHILD_ADMIN_REQUESTED,
    INSPIRE,
    CAMPUS,
    CHILD_INSPIRE_CAMPUS_PARTNER_REQUESTED,
    CHILD_INSPIRE_PARTNER_REQUESTED,
    REMOVE_CHILD_ADMIN_REQUESTED,
    CREATE_PARTNER_REQUESTED,
    DELETE_INVITE_REQUESTED,
    USERS_REQUESTED,
    ADD_USERS_REQUESTED,
    REMOVE_USER_REQUESTED,
    RESEND_INVITE,
} from './constants';
import {
    getPartnerAdmins,
    deletePartnerAdmin,
    createPartnerAdmin,
    getCScientist,
    addCScientist,
    deleteCScientist,
    getChildPartners,
    createChildPartner,
    deletePartnerInvite,
    getPartnerUsers,
    addPartnerUsers,
    resendPartnerInvite,
} from '../../api';
import {
    adminsSucceeded,
    adminsFailed,
    adminsRequested,
    deleteAdminsSucceeded,
    deleteAdminsFailed,
    addAdminsSucceeded,
    addAdminsFailed,
    citizenScientistSucceeded,
    citizenScientistFailed,
    addCitizenScientistSucceeded,
    addCitizenScientistFailed,
    citizenScientistRequested,
    deleteCitizenScientistSucceeded,
    deleteCitizenScientistFailed,
    addChildAdminSucceeded,
    addChildAdminFailed,
    childInspirePartnerSucceeded,
    childInspirePartnerFailed,
    childInspireCampusPartnerSucceeded,
    childInspireCampusPartnerFailed,
    removeChildAdminSucceeded,
    removeChildAdminFailed,
    createPartnerSucceeded,
    createPartnerFailed,
    childInspirePartnerRequested,
    childInspireCampusPartnerRequested,
    deleteInviteSucceeded,
    deleteInviteFailed,
    usersFailed,
    usersSucceeded,
    addUserFailed,
    addUserSucceeded,
    usersRequested,
    removeUsersSucceeded,
    removeUsersFailed,
} from './actions';
import { parseClientError } from '../../api/utils';
import {
    getPartnerId,
    getDeleteAdminID,
    getAddAdminEmail,
    getCitizenScientistEmail,
    getDeletedScientistID,
    getChildAdminEmail,
    getChildId,
    getRemoveChildId,
    getRemoveChildAdminId,
    getPartnerDetails,
    getUserId,
    getChildType,
} from './selectors';
import { getUserData } from '../auth/selectors';
import { logException } from '../../logger';

function* getAdmins() {
    try {
        const partnerId = yield select(getPartnerId);
        let admins = yield call(getPartnerAdmins, { partnerId });
        const userId = yield select(getUserId);

        var dataObj = [];
        for (let admin of admins.data.users) {
            if (admin.id === userId) {
                admin.fullName = admin.fullName + ' (You)';
                dataObj.unshift(admin);
            } else {
                dataObj.push(admin);
            }
        }

        admins.data.users = dataObj;
        yield put(adminsSucceeded(admins.data));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(adminsFailed(message));
    }
}

function* getUsers() {
    try {
        const partnerId = yield select(getPartnerId);
        let admins = yield call(getPartnerUsers, { partnerId });
        const userId = yield select(getUserId);

        var dataObj = [];
        for (let admin of admins.data.users) {
            if (admin.isCreator) admin.fullName = admin.fullName + ' (Owner)';
            if (admin.id === userId) {
                admin.fullName = admin.fullName + ' (You)';
                dataObj.unshift(admin);
            } else {
                dataObj.push(admin);
            }
        }

        admins.data.users = dataObj;
        yield put(usersSucceeded(admins.data));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(usersFailed(message));
    }
}

function* deleteUserAccount(action) {
    try {
        const partnerId = yield select(getPartnerId);
        const response = yield call(deletePartnerInvite, {
            receiverEmail: action.email,
            partnerId,
        });
        yield put(usersRequested());
        yield put(removeUsersSucceeded(response.message));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(removeUsersFailed(message));
    }
}

function* deleteAdminAccount() {
    try {
        const userId = yield select(getDeleteAdminID);
        const partnerId = yield select(getPartnerId);
        const response = yield call(deletePartnerAdmin, { userId, partnerId });
        yield put(adminsRequested());
        yield put(deleteAdminsSucceeded(response.message));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(deleteAdminsFailed(message));
    }
}

function* addAdminAccount() {
    try {
        const email = yield select(getAddAdminEmail);
        const partnerId = yield select(getPartnerId);
        const response = yield call(createPartnerAdmin, { partnerId, email });
        yield put(adminsRequested());
        yield put(addAdminsSucceeded(response.message));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addAdminsFailed(message));
    }
}

function* addUserAccount(action) {
    try {
        const { email, roles } = action;
        const partnerId = yield select(getPartnerId);
        const response = yield call(addPartnerUsers, {
            partnerId,
            email,
            roleType: roles,
        });
        yield put(usersRequested());
        yield put(addUserSucceeded(response.message));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addUserFailed(message));
    }
}

function* getPartnerScientist() {
    try {
        const partnerId = yield select(getPartnerId);
        const scientists = yield call(getCScientist, { partnerId });
        yield put(citizenScientistSucceeded(scientists.data));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(citizenScientistFailed(message));
    }
}

function* addCitizenScientist() {
    try {
        let partnerId = yield select(getPartnerId);
        let email = yield select(getCitizenScientistEmail);
        let response = yield call(addCScientist, { partnerId, email });
        yield put(addCitizenScientistSucceeded(response.message));
        yield put(citizenScientistRequested());
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);

        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addCitizenScientistFailed(message));
    }
}

function* deleteScientist() {
    try {
        let partnerId = yield select(getPartnerId);
        let userId = yield select(getDeletedScientistID);

        let response = yield call(deleteCScientist, { partnerId, userId });
        yield put(deleteCitizenScientistSucceeded(response.message));
        yield put(citizenScientistRequested());
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(deleteCitizenScientistFailed(message));
    }
}

function* getPartnerInspireChild() {
    try {
        let response = yield call(getChildPartners, {
            partnerTypeKey: INSPIRE,
        });
        let inspire = response.data;
        let ids = [];
        inspire.partners.forEach((element) => {
            ids.push(element.id);
        });
        for (var key in ids) {
            const result = yield call(getPartnerAdmins, {
                partnerId: ids[key],
            });
            inspire.partners[key].users = result.data.users;
            inspire.partners[key].licenseCount = result.data.licenseCount;
        }
        yield put(childInspirePartnerSucceeded(inspire));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(childInspirePartnerFailed(message));
        yield put(deleteCitizenScientistFailed(message));
    }
}
function* getPartnerCampusChild() {
    try {
        // let response = yield call(getChildPartners, {partnerTypeKey:CAMPUS});
        let response = yield call(getChildPartners, { partnerTypeKey: CAMPUS });
        let campus = response.data;
        let ids = [];
        campus.partners.forEach((element) => {
            ids.push(element.id);
        });
        for (var key in ids) {
            const result = yield call(getPartnerAdmins, {
                partnerId: ids[key],
            });
            campus.partners[key].users = result.data.users;
            campus.partners[key].licenseCount = result.data.licenseCount;
        }
        yield put(childInspireCampusPartnerSucceeded(campus));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(childInspireCampusPartnerFailed(message));
        yield put(deleteCitizenScientistFailed(message));
    }
}

function* addChildPartnerAdmin() {
    const partnerId = yield select(getChildId);
    let email = yield select(getChildAdminEmail);
    try {
        const response = yield call(createPartnerAdmin, { partnerId, email });
        yield put(addChildAdminSucceeded(response.message));
        const partnerType = yield select(getChildType);
        switch (partnerType) {
            case 'Inspire':
                yield put(childInspirePartnerRequested());
                break;
            case 'Campus':
                yield put(childInspireCampusPartnerRequested());
                break;
            default:
                break;
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(addChildAdminFailed(partnerId, message));
    }
}

function* removeChildAdmin() {
    const partnerId = yield select(getRemoveChildId);
    const userId = yield select(getRemoveChildAdminId);
    try {
        const response = yield call(deletePartnerAdmin, { userId, partnerId });
        yield put(removeChildAdminSucceeded(response.message));
        const partnerType = yield select(getChildType);
        switch (partnerType) {
            case 'Inspire':
                yield put(childInspirePartnerRequested());
                break;
            case 'Campus':
                yield put(childInspireCampusPartnerRequested());
                break;
            default:
                break;
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(removeChildAdminFailed(partnerId, message));
    }
}

function* createNewPartner() {
    const partnerDetails = yield select(getPartnerDetails);
    try {
        if (typeof partnerDetails.banner != 'string')
            delete partnerDetails.banner;
        const response = yield call(createChildPartner, partnerDetails);
        yield put(createPartnerSucceeded(response.message));
        if (partnerDetails.partnerTypeKey === INSPIRE) {
            yield put(childInspirePartnerRequested());
        } else if (partnerDetails.partnerTypeKey === CAMPUS) {
            yield put(childInspireCampusPartnerRequested());
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(createPartnerFailed(message, partnerDetails.partnerTypeKey));
    }
}

function* deleteInvite(action) {
    try {
        let partnerId = action.partnerId || (yield select(getPartnerId));
        let receiverEmail = action.email;
        let response = yield call(deletePartnerInvite, {
            receiverEmail,
            partnerId,
            roleTypeId: action.sectionType === 'Scientist' ? 2 : 3,
        });
        switch (action.sectionType) {
            case 'Admin':
                yield put(adminsRequested());
                break;
            case 'Scientist':
                yield put(citizenScientistRequested());
                break;
            case 'Inspire':
                yield put(childInspirePartnerRequested());
                break;
            case 'Campus':
                yield put(childInspireCampusPartnerRequested());
                break;
            default:
                break;
        }
        yield put(deleteInviteSucceeded(response.message));
    } catch (e) {
        Sentry.captureException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(deleteInviteFailed(message));
    }
}

function* resendInvite(action) {
    let email = action.email;
    yield call(resendPartnerInvite, {
        email,
    });
}

export default function*() {
    yield all([yield takeLatest(RESEND_INVITE, resendInvite)]);
    yield all([yield takeLatest(REMOVE_USER_REQUESTED, deleteUserAccount)]);
    yield all([yield takeLatest(ADD_USERS_REQUESTED, addUserAccount)]);
    yield all([yield takeLatest(USERS_REQUESTED, getUsers)]);
    yield all([yield takeLatest(ADMINS_REQUESTED, getAdmins)]);
    yield all([yield takeLatest(DELETE_ADMINS_REQUESTED, deleteAdminAccount)]);
    yield all([yield takeLatest(ADD_ADMINS_REQUESTED, addAdminAccount)]);
    yield all([
        yield takeLatest(CITIZEN_SCIENTIST_REQUESTED, getPartnerScientist),
    ]);
    yield all([
        yield takeLatest(ADD_CITIZEN_SCIENTIST_REQUESTED, addCitizenScientist),
    ]);
    yield all([
        yield takeLatest(DELETE_CITIZEN_SCIENTIST_REQUESTED, deleteScientist),
    ]);
    yield all([
        yield takeLatest(
            CHILD_INSPIRE_PARTNER_REQUESTED,
            getPartnerInspireChild,
        ),
    ]);
    yield all([
        yield takeLatest(
            CHILD_INSPIRE_CAMPUS_PARTNER_REQUESTED,
            getPartnerCampusChild,
        ),
    ]);
    yield all([
        yield takeLatest(ADD_CHILD_ADMIN_REQUESTED, addChildPartnerAdmin),
    ]);
    yield all([
        yield takeLatest(REMOVE_CHILD_ADMIN_REQUESTED, removeChildAdmin),
    ]);
    yield all([yield takeLatest(CREATE_PARTNER_REQUESTED, createNewPartner)]);
    yield all([yield takeLatest(DELETE_INVITE_REQUESTED, deleteInvite)]);
}
